import { useEffect } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import {
  hideAdGroupWeeklyScheduleEdit,
  hidePreview,
  setImpressionAll,
  setWeeklySchedule,
  showPreview,
  updateAdGroupsWeeklySchedule,
} from 'features/dashboard/operation/adGroupWeeklyScheduleEdit/adGroupWeeklyScheduleEditSlice'
import { Modal } from 'components/dialog/Modals'
import { PreviewConfirmModal } from 'components/dialog/ModalTemplate'
import { isEmptyObject } from 'utils'
import { fail, failWithErrorResponse, success } from 'features/toast/Toast'
import useValidator from 'utils/hooks/useValidator'
import { refreshDashboard } from 'features/dashboard/dashboardCommonSlice'
import Loading from 'components/loading/Loading'
import RadioButton from 'components/form/RadioButton'
import TimeTable, { everyTime, noneTime, toBit } from 'components/TimeTable'
import Table from 'components/table/Table'
import classNames from 'classnames'
import WeeklyScheduleTooltip from './WeeklyScheduleTooltip'
import FixedTable from 'components/table/FixedTable'

const AdGroupWeeklyScheduleEditModal = () => {
  const dispatch = useDispatch()
  const {
    visible,
    isSubmitting,
    previewVisible,
    isLoading,
    adGroups,
    loadingError,
    form: { impressionAll, weeklySchedule },
    preview: {
      impressionAll: previewImpressionAll,
      weeklySchedule: previewWeeklySchedule,
    },
  } = useSelector(
    ({ dashboard }) => dashboard.operation.adGroupWeeklyScheduleEdit,
    shallowEqual
  )
  const { errors, validate } = useValidator(
    () => ({
      weeklySchedule:
        !impressionAll &&
        weeklySchedule === noneTime &&
        '집행기간의 요일/시간 설정을 확인하세요. 반드시 한 개 이상 설정해야 합니다.',
    }),
    [impressionAll, weeklySchedule]
  )
  const handlePreview = async () => {
    try {
      await validate()
      if (isEmptyObject(errors)) {
        dispatch(showPreview())
      }
    } catch (ignore) {}
  }
  const handleConfirm = async ({ preventClose }) => {
    try {
      await validate()
      if (isEmptyObject(errors)) {
        const { error } = await dispatch(updateAdGroupsWeeklySchedule())
        if (error) {
          preventClose()
          fail(
            '광고그룹 요일/시간 변경사항을 적용할 수 없습니다.\n다시 시도해 주세요.'
          )
        } else {
          dispatch(refreshDashboard())
          success('광고그룹 요일/시간 변경사항이 적용되었습니다.')
        }
      }
    } catch (error) {
      preventClose()
    }
  }

  useEffect(() => {
    if (!isEmptyObject(errors)) {
      dispatch(hidePreview())
    }
  }, [dispatch, errors])
  useEffect(
    () => () => {
      dispatch(hideAdGroupWeeklyScheduleEdit())
    },
    [dispatch]
  )
  useEffect(() => {
    if (loadingError) {
      dispatch(hideAdGroupWeeklyScheduleEdit())
      failWithErrorResponse(loadingError, {
        messageMap: {
          104007: '삭제된 값이 존재합니다.\n새로고침 후 다시 시도해 주세요.',
        },
      })
    }
  }, [dispatch, loadingError])

  const preview = previewVisible && isEmptyObject(errors)
  const changed =
    impressionAll !== previewImpressionAll ||
    weeklySchedule !== previewWeeklySchedule

  return (
    <Modal on={visible} className="day_layer">
      <PreviewConfirmModal
        title="광고그룹 요일/시간 변경"
        isSubmitting={isSubmitting}
        onPreview={handlePreview}
        onConfirm={handleConfirm}
        previewDisabled={!changed && preview}
        confirmDisabled={changed && preview}
        handleClose={() => dispatch(hideAdGroupWeeklyScheduleEdit())}>
        <p className="desc_copy">
          광고가 노출되는 요일과 시간을 설정합니다. 설정된 요일과 시간에만
          광고가 노출됩니다.
        </p>
        {isLoading ? (
          <Loading />
        ) : (
          <>
            <ul className="list_radiocheck">
              <li>
                <RadioButton
                  label="모든 요일/시간 노출"
                  checked={impressionAll}
                  onValueChange={() => dispatch(setImpressionAll(true))}
                />
              </li>
              <li>
                <RadioButton
                  label="상세설정"
                  checked={!impressionAll}
                  onValueChange={() => dispatch(setImpressionAll(false))}
                />
              </li>
            </ul>
            {!impressionAll && (
              <>
                <TimeTable
                  headerWidth={69}
                  weeklySchedule={weeklySchedule}
                  onChange={v => dispatch(setWeeklySchedule(v))}
                  error={errors.weeklySchedule}
                />
                {errors.weeklySchedule && (
                  <p className="txt_error">{errors.weeklySchedule}</p>
                )}
              </>
            )}
            {preview && (
              <FixedTable caption="광고그룹 집행일자 변경 이력 표">
                <Table.Head>
                  <Table.Column
                    title="선택 광고그룹"
                    className="inner_cell"
                    width={193}
                  />
                  <Table.Column
                    title="변경 전"
                    className="inner_cell"
                    width={174}
                  />
                  <Table.Column title="변경 후" className="inner_cell" />
                </Table.Head>
                <Table.Body>
                  {adGroups.map(({ id, name, impressionSchedule }) => {
                    const current = toBit(impressionSchedule.weeklySchedule)
                    return (
                      <Table.Row
                        rowId={id}
                        key={id}
                        className={classNames({
                          update: previewImpressionAll
                            ? current !== everyTime
                            : current !== previewWeeklySchedule,
                        })}>
                        <td>
                          <div className="inner_cell">
                            <span className="txt_limit2">{name}</span>
                          </div>
                        </td>
                        <td>
                          <div className="inner_cell">
                            {current === everyTime ? (
                              '모든 요일/시간 노출'
                            ) : (
                              <WeeklyScheduleTooltip weeklySchedule={current} />
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="inner_cell">
                            {previewImpressionAll ||
                            previewWeeklySchedule === everyTime ? (
                              '모든 요일/시간 노출'
                            ) : (
                              <WeeklyScheduleTooltip
                                weeklySchedule={previewWeeklySchedule}
                              />
                            )}
                          </div>
                        </td>
                      </Table.Row>
                    )
                  })}
                </Table.Body>
              </FixedTable>
            )}
          </>
        )}
      </PreviewConfirmModal>
    </Modal>
  )
}
export default AdGroupWeeklyScheduleEditModal
